import { useEffect, useState } from "react";
import { useEntregador } from "../context/EntregadorContext";
import EntregadorCard from "../components/EntregadorCard";
import { Clock, CheckCircle } from "lucide-react";
import { Usuario } from "../../../types/usuario";
import { FilaEntrega } from "../../../services/filaEntregaService";
import { Entrega } from "../../../services/entregaService";
import {
  getMsRestantesParaFinalizar,
  MIN_MINUTOS_FINALIZAR_ENTREGA,
} from "../entregaTempoMinimo";

interface EntregadorOperandoItem {
  entregador: Usuario;
  filaEntrega: FilaEntrega;
  entregaAtiva?: Entrega;
}

const Operando = () => {
  const {
    entregadoresOperando,
    handleFinalizar,
    isFinalizando,
    filaFinalizandoId,
    isLoadingOperando,
  } = useEntregador();

  // Reavalia a trava de tempo mínimo a cada segundo.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (isLoadingOperando) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-6 text-center sm:p-8">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <p className="text-sm text-neutral-500 sm:text-base">
          Carregando entregadores em operação...
        </p>
      </div>
    );
  }

  if (entregadoresOperando.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-6 text-center sm:p-8">
        <Clock className="mx-auto mb-4 text-neutral-400" size={48} />
        <p className="text-sm text-neutral-500 sm:text-base">
          Nenhum entregador está operando no momento.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {entregadoresOperando.map(
        ({ entregador, filaEntrega, entregaAtiva }: EntregadorOperandoItem) => {
          if (!entregador || !filaEntrega) return null;

          const temEntregaAtiva =
            filaEntrega.status === "em_andamento" || !!entregaAtiva;
          const isThisCardFinalizando = filaFinalizandoId === filaEntrega.id;
          const msRestantes = getMsRestantesParaFinalizar(
            entregaAtiva?.dataInicio
          );
          const bloqueadoPorTempo = temEntregaAtiva && msRestantes > 0;
          const minutosRestantes = Math.ceil(msRestantes / 60_000);
          const actionText = isThisCardFinalizando
            ? "Finalizando..."
            : bloqueadoPorTempo
              ? `Aguarde ${minutosRestantes} min`
              : "Finalizar Entrega";

          const buttonClassName =
            "w-full transform rounded-lg bg-success px-4 py-2 text-sm font-bold text-white shadow-lg transition duration-300 hover:scale-105 hover:bg-success-dark hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-2 disabled:opacity-50 sm:py-3 sm:text-base";

          return (
            <EntregadorCard
              key={filaEntrega.id}
              entregador={entregador}
              variant="operando"
              filaEntrega={filaEntrega}
              entregaAtiva={entregaAtiva}
              showAction={temEntregaAtiva}
              onAction={() =>
                handleFinalizar(
                  filaEntrega.id!,
                  entregaAtiva?.id,
                  filaEntrega.status,
                  !!entregaAtiva
                )
              }
              isActionLoading={isFinalizando}
              isFinalizando={isThisCardFinalizando}
              actionDisabled={bloqueadoPorTempo}
              actionDisabledReason={
                bloqueadoPorTempo
                  ? `Só é possível finalizar após ${MIN_MINUTOS_FINALIZAR_ENTREGA} minutos em rota.`
                  : undefined
              }
              actionText={actionText}
              actionIcon={<CheckCircle size={16} />}
              actionButtonClassName={buttonClassName}
            />
          );
        }
      )}
    </div>
  );
};

export default Operando;
